import type { RowWarning, TenantRow } from '../schemas/bulk-migration';

/**
 * The module's pre-run check findings on a client row, apart from the rest of
 * its message.
 *
 * The runner keeps them twice: as the row's `warnings` (`{ code, message,
 * blocking }`) and appended to `message` (`… Warnings: CODE — message; Will
 * block at launch: CODE — message`) for a reader that knows only `message`.
 * The structured field wins; a row without it (an older API) is read from the
 * text. A dry-run's blocking finding never changes its outcome — the row stays
 * `planned` — so it is the only place the operator learns the launch will
 * refuse that client.
 */

/** The runner's prefix of a blocking finding (`WILL_BLOCK_AT_LAUNCH`). */
export const WILL_BLOCK_AT_LAUNCH = 'Will block at launch';

/** Every `Warnings: ` that could start the runner's suffix; the LAST one is read. */
const WARNINGS_MARKER = /(?:^|\s)Warnings: /g;
/**
 * What a runner code looks like: a letter, then letters, digits and `_.:-`, and
 * either all capitals (`SHOP_INVENTORY_OPENING_COST_NOT_APPROVED`) or holding an
 * `_` (`validate_failed`). Plain words (`Inventory — see settings`) are not
 * codes, so a finding's own message is not split at them.
 */
const CODE = '(?:[A-Z][A-Z0-9_.:-]*|[A-Za-z][A-Za-z0-9.:-]*_[A-Za-z0-9_.:-]*)';
const CODE_PATTERN = new RegExp(`^${CODE}$`);
/** `; ` only where the next finding starts: a finding's own message may hold one. */
const ENTRY_SEPARATOR = new RegExp(`; (?=(?:Will block at launch: )?${CODE} — )`);
const BLOCKING_PREFIX = `${WILL_BLOCK_AT_LAUNCH}: `;
const CODE_SEPARATOR = ' — ';

export interface RowMessage {
    /** The message without its findings; `null` when nothing else is left. */
    readonly text: string | null;
    readonly warnings: readonly RowWarning[];
}

/** One finding as one line, the way the runner writes it. */
export function warningLine(warning: RowWarning): string {
    const entry = `${warning.code}${CODE_SEPARATOR}${warning.message}`;
    return warning.blocking ? `${BLOCKING_PREFIX}${entry}` : entry;
}

/** Every finding as the runner appends them (`Warnings: a; b`), or `null` when none. */
export function warningsText(warnings: readonly RowWarning[]): string | null {
    if (warnings.length === 0) return null;
    return `Warnings: ${warnings.map(warningLine).join('; ')}`;
}

/** What the table labels a finding with. */
export function warningLabel(warning: RowWarning): string {
    return warning.blocking ? WILL_BLOCK_AT_LAUNCH : 'Warning';
}

/**
 * The row's message split from its findings. The structured field wins (the API
 * sends `[]` for a row without findings, so its text is never parsed); only a row
 * without the field (an older API) is read from the text, from the LAST
 * `Warnings: `. A message whose findings cannot all be read back is returned
 * whole, with none: hiding text is worse than showing it raw.
 */
export function rowMessage(row: Pick<TenantRow, 'message' | 'warnings'>): RowMessage {
    const message = row.message;
    if (row.warnings !== undefined) {
        return { text: withoutSuffix(message, warningsText(row.warnings)), warnings: row.warnings };
    }
    if (message === null) return { text: null, warnings: [] };

    const marker = [...message.matchAll(WARNINGS_MARKER)].at(-1);
    if (marker === undefined) return { text: message, warnings: [] };

    const warnings = parseEntries(message.slice(marker.index + marker[0].length));
    if (warnings === null) return { text: message, warnings: [] };
    return { text: blankToNull(message.slice(0, marker.index)), warnings };
}

function parseEntries(tail: string): RowWarning[] | null {
    const warnings: RowWarning[] = [];
    for (const raw of tail.split(ENTRY_SEPARATOR)) {
        const blocking = raw.startsWith(BLOCKING_PREFIX);
        const entry = blocking ? raw.slice(BLOCKING_PREFIX.length) : raw;
        const at = entry.indexOf(CODE_SEPARATOR);
        const code = at < 0 ? '' : entry.slice(0, at);
        if (!CODE_PATTERN.test(code)) return null;
        warnings.push({
            code,
            message: entry.slice(at + CODE_SEPARATOR.length),
            blocking,
        });
    }
    return warnings;
}

/** The message minus the findings the runner appended, when it ends with exactly them. */
function withoutSuffix(message: string | null, suffix: string | null): string | null {
    if (message === null || suffix === null || !message.endsWith(suffix)) return message;
    return blankToNull(message.slice(0, message.length - suffix.length));
}

function blankToNull(text: string): string | null {
    const trimmed = text.trim();
    return trimmed.length === 0 ? null : trimmed;
}
